import math from './math';
import random from './random';

type Presets = 'avatar';

type RandomColorParams = {
  saturation?: number | [number, number];
  lightness?: number | [number, number];
  lightColor?: string;
  darkColor?: string;
  shift?: number;
  preset?: Presets;
};

type ContrastingColorParams = {
  dark?: string;
  light?: string;
  threshold?: number;
};

const PRESETS: Record<Presets, RandomColorParams> = {
  avatar: { saturation: 80, lightness: 60, shift: 250 },
};

const GREY = '#818288';

/**
 * Converts a hex color code to an RGB tuple.
 * @param hex - The hex color code (e.g., "#FFFFFF" or "#FFF").
 * @returns An array containing the RGB values.
 */
const hexToRgb = (input: string): [number, number, number] => {
  // Remove the hash (#) at the start if it's there
  let hex = input.replace(/^#/, '');

  // Handle shorthand hex codes (e.g., "#FFF" becomes "#FFFFFF")
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('');
  }

  const bigint = Number.parseInt(hex, 16);
  return [
    (bigint >> 16) & 255, // Red
    (bigint >> 8) & 255, // Green
    bigint & 255, // Blue
  ];
};

/**
 * Calculates the relative luminance of a color using its RGB values.
 * @param r - Red value (0-255).
 * @param g - Green value (0-255).
 * @param b - Blue value (0-255).
 * @returns The relative luminance (0.0 - 1.0).
 */
const luminance = (r: number, g: number, b: number): number => {
  // Convert the RGB values to the range 0.0 - 1.0
  const rgb = [r, g, b].map((value) => {
    const channel = value / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });

  // Calculate the luminance using the weighted sum formula
  return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
};

/**
 * Calculates the relative contrast ratio of two colors.
 * @param r - Red value (0-255).
 * @param g - Green value (0-255).
 * @param b - Blue value (0-255).
 * @returns The contrast ratio (1.0 - 21.0).
 */
const contrast = (foreground: string, background: string): number => {
  const rgb1 = hexToRgb(foreground);
  const rgb2 = hexToRgb(background);

  const lum1 = luminance(...rgb1) + 0.05;
  const lum2 = luminance(...rgb2) + 0.05;

  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);

  const ret = brightest / darkest;
  return ret;
};

/**
 * Determines whether black or white has better contrast with the input color.
 * @param color - The hex color code (e.g., "#FFFFFF").
 * @param params.threshold - Minimum contrast difference required to pick dark over light (default 0).
 *   - threshold = 0: pure WCAG comparison, dark wins if it has any higher contrast.
 *   - threshold > 0: biases toward light, dark must win by at least this margin.
 *   - threshold < 0: biases toward dark, light must win by at least this margin.
 * @returns "black" or "white" based on the contrast.
 */
const getContrastingColor = (color: string, params?: ContrastingColorParams): string => {
  const dark = params?.dark ?? '#000000';
  const light = params?.light ?? '#FFFFFF';
  const threshold = params?.threshold ?? 0;

  const contrastDark = contrast(color, dark);
  const contrastLight = contrast(color, light);

  return contrastDark - contrastLight > threshold ? dark : light;
};

/**
 * Converts an HSL color value to HEX format.
 * @param h - The hue (0-360).
 * @param s - The saturation (0-100).
 * @param l - The lightness (0-100).
 * @returns The HEX color code (e.g., "#FFFFFF").
 */
const hslToHex = (H: number, S: number, L: number): string => {
  // Normalize saturation and lightness to 0-1 range
  const h = H % 360;
  const s = (S % 100) / 100;
  const l = (L % 100) / 100;

  const c = (1 - Math.abs(2 * l - 1)) * s; // Chroma
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1)); // Intermediate value
  const m = l - c / 2;

  let r = 0;
  let g = 0;
  let b = 0;

  if (0 <= h && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (60 <= h && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (120 <= h && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (180 <= h && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (240 <= h && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (300 <= h && h < 360) {
    r = c;
    g = 0;
    b = x;
  }

  // Convert r, g, b to values between 0-255
  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  b = Math.round((b + m) * 255);

  // Convert RGB to HEX
  const toHex = (n: number) => n.toString(16).padStart(2, '0');

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const seedColor = (word?: string, params?: RandomColorParams): [string, string] => {
  const DEFAULT_SATURATION = 50;
  const DEFAULT_LIGHTNESS = 50;
  if (!word) return [GREY, '#FFFFFF'];
  const rnd = random.pseudo(word);
  let { saturation: s, lightness: l, shift, darkColor, lightColor } = params ?? {};

  if (params?.preset && PRESETS[params.preset]) {
    const preset = PRESETS[params.preset];
    s = preset.saturation;
    l = preset.lightness;
    shift = preset.shift;
    darkColor = preset.darkColor;
    lightColor = preset.lightColor;
  }

  const S: [number, number] = Array.isArray(s)
    ? s
    : [s ?? DEFAULT_SATURATION, s ?? DEFAULT_SATURATION];
  const L: [number, number] = Array.isArray(l)
    ? l
    : [l ?? DEFAULT_LIGHTNESS, l ?? DEFAULT_LIGHTNESS];

  const hue = math.lerp(rnd, 0, 1, 0, 360) + (shift ?? 0);
  const saturation = math.lerp(rnd, 0, 1, S[0], S[1]);
  const lightness = math.lerp(rnd, 0, 1, L[0], L[1]);

  const color = hslToHex(hue, saturation, lightness);
  const text = getContrastingColor(color, {
    dark: darkColor,
    light: lightColor,
  });

  return [color, text];
};

export default {
  hexToRgb,
  luminance,
  getContrastingColor,
  hslToHex,
  seedColor,
  contrast,
};
